import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChevronLeft,
  CheckCircle2,
  Clock,
  Loader2,
  PlayCircle,
  RefreshCcw,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { useTransferOperations } from "../../hooks/useTransferOperations";
import { usePermissions, PERMISSIONS } from "../../hooks/usePermissions";
import { ConfirmActionModal } from "./ConfirmActionModal";
import { readApiError } from "../../utils/readApiError";

/**
 * Staff view of a single transfer.
 *
 * Previously an operator could see that a transfer was stuck but had to drop to
 * an API client to do anything about it. This surfaces the three-leg execution
 * (onramp → stablecoin settlement → offramp), the immutable status timeline,
 * and the manual operations that can move it forward.
 *
 * Forcing a leg terminal asserts an outcome the provider has not confirmed by
 * webhook, so those actions are separated from the ordinary "execute" path and
 * ask for a reason.
 */
export function AdminTransferDetail() {
  const { id } = useParams();
  const [pendingAction, setPendingAction] = useState(null);
  const [actionError, setActionError] = useState(null);

  const { can } = usePermissions();
  const canExecute = can(PERMISSIONS.TRANSFERS_EXECUTE);

  const {
    transfer,
    isLoading,
    error,
    refetch,
    isFetching,
    executeLeg,
    markLegCompleted,
    markLegFailed,
    confirmSettlementFunded,
    resumeFundingHold,
    isActing,
  } = useTransferOperations(id);

  async function runPendingAction(reason) {
    if (!pendingAction) return;
    setActionError(null);
    try {
      await pendingAction.run(reason);
      setPendingAction(null);
    } catch (err) {
      setActionError(readApiError(err, "Could not complete that action."));
    }
  }

  if (isLoading) {
    return <CenteredNote text="Loading transfer…" />;
  }

  if (error || !transfer) {
    return (
      <div className="space-y-4">
        <BackLink />
        <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-sm text-red-600">
          Could not load this transfer.
        </div>
      </div>
    );
  }

  const legs = transfer.legs ?? [];
  const events = [...(transfer.status_events ?? [])].sort(
    (a, b) => new Date(b.occurred_at || b.created_at) - new Date(a.occurred_at || a.created_at)
  );

  return (
    <div className="space-y-8 text-left">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <BackLink />
          <h1 className="text-2xl md:text-3xl text-[#0A4730] font-serif font-semibold mt-2 font-mono">
            {transfer.public_reference}
          </h1>
          <div className="mt-2">
            <StatusBadge status={transfer.status} />
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-4 py-3 rounded-2xl border border-slate-200 bg-white text-[#0A4730] font-semibold text-xs disabled:opacity-50 hover:bg-slate-50 transition"
          >
            <RefreshCcw size={15} className={isFetching ? "animate-spin" : ""} />
            Refresh
          </button>
          {canExecute && (
            <button
              onClick={() =>
                setPendingAction({
                  title: "Resume this transfer?",
                  description:
                    "Resumes a transfer whose AML funding hold has elapsed. An active hold cannot be overridden - if the window has not passed, this is refused and tells you when it will.",
                  confirmLabel: "Resume transfer",
                  requireReason: true,
                  // The reason used to be collected and dropped on the floor:
                  // this called releaseFundingHold() with no argument.
                  run: (reason) => resumeFundingHold({ reason }),
                })
              }
              className="inline-flex items-center gap-2 px-4 py-3 rounded-2xl bg-[#0A4730] text-white font-semibold text-xs hover:bg-[#0A4730]/90 transition"
            >
              <ShieldCheck size={15} /> Resume transfer
            </button>
          )}
        </div>
      </div>

      {actionError && !pendingAction && (
        <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-sm text-red-600">
          {actionError}
        </div>
      )}

      {/* Commercials */}
      <Panel title="Transfer">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 text-sm">
          <Detail label="Send" value={money(transfer.send_amount, transfer.send_currency)} />
          <Detail
            label="Receive"
            value={
              transfer.receive_amount
                ? money(transfer.receive_amount, transfer.receive_currency)
                : `${transfer.receive_currency} pending`
            }
          />
          <Detail label="Fee" value={transfer.fee_amount ? money(transfer.fee_amount, transfer.fee_currency) : "—"} />
          <Detail label="Rate" value={transfer.exchange_rate || "—"} />
          <Detail label="Funding method" value={transfer.funding_method || "—"} />
          <Detail label="Created" value={formatDate(transfer.created_at)} />
          <Detail label="Completed" value={formatDate(transfer.completed_at)} />
          <Detail label="Failure reason" value={transfer.failure_reason || "None"} />
        </div>
        <Link
          to={`/admin/users?search=${encodeURIComponent(transfer.sender_user_id)}`}
          className="mt-5 inline-block text-xs font-semibold text-[#0A4730] underline underline-offset-2"
        >
          View sender account
        </Link>
      </Panel>

      {/* Legs */}
      <Panel title="Execution legs">
        {legs.length === 0 ? (
          <p className="text-sm text-slate-400 py-2">
            No legs created yet - the transfer has not been confirmed and routed.
          </p>
        ) : (
          <div className="space-y-3">
            {legs.map((leg) => (
              <LegRow
                key={leg.id}
                leg={leg}
                canExecute={canExecute}
                isActing={isActing}
                onExecute={() =>
                  setPendingAction({
                    title: `Execute the ${humanize(leg.leg_type)} leg?`,
                    description:
                      "Submits this leg to its provider. Safe to run for a pending leg.",
                    confirmLabel: "Execute leg",
                    requireReason: false,
                    run: () => executeLeg(leg.id),
                  })
                }
                onComplete={() =>
                  setPendingAction({
                    title: `Force the ${humanize(leg.leg_type)} leg complete?`,
                    description:
                      "This asserts an outcome the provider has not confirmed by webhook, and posts ledger entries. Only do this with out-of-band evidence that the money moved.",
                    confirmLabel: "Mark completed",
                    destructive: true,
                    requireReason: true,
                    run: (reason) =>
                      markLegCompleted({ legId: leg.id, payload: { reason } }),
                  })
                }
                onFail={() =>
                  setPendingAction({
                    title: `Force the ${humanize(leg.leg_type)} leg failed?`,
                    description:
                      "Marks the leg and the transfer as failed. This is terminal.",
                    confirmLabel: "Mark failed",
                    destructive: true,
                    requireReason: true,
                    // The endpoint requires failure_code and failure_message.
                    // Sending only `reason` was rejected with a 422 on every
                    // attempt, so this action never once worked from the UI.
                    run: (reason) =>
                      markLegFailed({
                        legId: leg.id,
                        payload: {
                          failure_code: "manual_operator_failure",
                          failure_message: reason,
                          reason,
                        },
                      }),
                  })
                }
                onSettlementFunded={() =>
                  setPendingAction({
                    title: "Confirm settlement funding?",
                    description:
                      "Records operator-verified prefunding for this cross-provider settlement, which releases the offramp.",
                    confirmLabel: "Confirm funded",
                    requireReason: true,
                    reasonLabel: "Funding reference",
                    reasonPlaceholder:
                      "Provider or bank reference for the funding you verified, e.g. TRX-88421",
                    run: (reason) =>
                      confirmSettlementFunded({
                        legId: leg.id,
                        payload: { funding_reference: reason },
                      }),
                  })
                }
              />
            ))}
          </div>
        )}
      </Panel>

      {/* Settlements */}
      {(transfer.stablecoin_settlements ?? []).length > 0 && (
        <Panel title="Stablecoin settlements">
          <div className="space-y-3">
            {transfer.stablecoin_settlements.map((settlement) => (
              <div
                key={settlement.id}
                className="rounded-2xl border border-slate-100 bg-[#FDFDFD] p-4 grid sm:grid-cols-4 gap-4 text-sm"
              >
                <Detail label="Asset" value={settlement.asset || "—"} />
                <Detail label="Amount" value={settlement.amount || "—"} />
                <Detail label="Mode" value={humanize(settlement.settlement_mode || "")} />
                <Detail label="Status" value={humanize(settlement.status || "")} />
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* Timeline */}
      <Panel title="Status timeline">
        {events.length === 0 ? (
          <p className="text-sm text-slate-400 py-2">No status events recorded.</p>
        ) : (
          <ol className="space-y-3">
            {events.map((event) => (
              <li key={event.id} className="flex gap-3">
                <div className="w-2 h-2 rounded-full bg-[#0A4730]/40 mt-2 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-[#0A2540]">
                    {event.previous_status
                      ? `${humanize(event.previous_status)} → `
                      : ""}
                    <span className="font-semibold">{humanize(event.new_status)}</span>
                  </p>
                  <p className="text-xs text-slate-400">
                    {formatDate(event.occurred_at || event.created_at)}
                    {event.source ? ` · ${humanize(event.source)}` : ""}
                    {event.provider ? ` · ${event.provider}` : ""}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </Panel>

      <ConfirmActionModal
        open={Boolean(pendingAction)}
        title={pendingAction?.title || ""}
        description={pendingAction?.description}
        confirmLabel={pendingAction?.confirmLabel}
        destructive={pendingAction?.destructive}
        requireReason={pendingAction?.requireReason}
        // Not every prompt is a "reason". Confirming settlement funding asks
        // for a payment reference, and storing prose in that field makes the
        // record useless for reconciliation.
        reasonLabel={pendingAction?.reasonLabel}
        reasonPlaceholder={pendingAction?.reasonPlaceholder}
        error={actionError}
        isSubmitting={isActing}
        onConfirm={runPendingAction}
        onCancel={() => {
          setPendingAction(null);
          setActionError(null);
        }}
      />
    </div>
  );
}

function LegRow({
  leg,
  canExecute,
  isActing,
  onExecute,
  onComplete,
  onFail,
  onSettlementFunded,
}) {
  const isSettlement = leg.leg_type === "stablecoin_settlement";
  const isPending = leg.status === "pending";
  const isTerminal = ["completed", "failed", "cancelled"].includes(leg.status);

  return (
    <div className="rounded-2xl border border-slate-100 bg-[#FDFDFD] p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-[#0A2540] capitalize">
            {humanize(leg.leg_type)}
          </p>
          <p className="text-xs text-slate-400">
            {leg.provider || "internal"}
            {leg.rail ? ` · ${leg.rail}` : ""}
            {leg.channel ? ` · ${leg.channel}` : ""}
          </p>
        </div>
        <LegStatusBadge status={leg.status} />
      </div>

      <div className="grid sm:grid-cols-3 gap-4 text-xs">
        <Detail
          label="Amounts"
          value={`${leg.source_amount ?? "—"} ${leg.source_currency || ""} → ${
            leg.destination_amount ?? "—"
          } ${leg.destination_currency || ""}`}
        />
        <Detail label="Provider ref" value={leg.provider_reference || "—"} />
        <Detail label="Failure" value={leg.failure_message || "—"} />
      </div>

      {canExecute && !isTerminal && (
        <div className="flex flex-wrap gap-2 pt-1">
          {isPending && !isSettlement && (
            <ActionButton onClick={onExecute} disabled={isActing} icon={PlayCircle}>
              Execute
            </ActionButton>
          )}
          {isSettlement && (
            <ActionButton onClick={onSettlementFunded} disabled={isActing} icon={ShieldCheck}>
              Confirm funded
            </ActionButton>
          )}
          <ActionButton onClick={onComplete} disabled={isActing} icon={CheckCircle2} subtle>
            Force complete
          </ActionButton>
          <ActionButton onClick={onFail} disabled={isActing} icon={XCircle} danger>
            Force fail
          </ActionButton>
        </div>
      )}
    </div>
  );
}

function ActionButton({ onClick, disabled, icon: Icon, children, danger, subtle }) {
  const tone = danger
    ? "border-red-200 text-red-600 hover:bg-red-50"
    : subtle
    ? "border-slate-200 text-[#0A2540]/70 hover:bg-slate-50"
    : "border-[#0A4730]/30 text-[#0A4730] hover:bg-[#F5F9F4]";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border bg-white text-xs font-semibold transition disabled:opacity-40 ${tone}`}
    >
      {disabled ? <Loader2 size={13} className="animate-spin" /> : <Icon size={13} />}
      {children}
    </button>
  );
}

function Panel({ title, children }) {
  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)]">
      <h3 className="text-sm font-semibold text-[#0A2540] mb-4">{title}</h3>
      {children}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="space-y-1 min-w-0">
      <p className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold">
        {label}
      </p>
      <div className="text-[#0A2540] font-medium break-all">{value}</div>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/admin/transfers"
      className="inline-flex items-center gap-1 text-xs font-semibold text-[#0A4730]/70 hover:text-[#0A4730]"
    >
      <ChevronLeft size={14} /> Back to transfers
    </Link>
  );
}

function CenteredNote({ text }) {
  return (
    <div className="min-h-[40vh] grid place-items-center text-sm text-[#0A2540]/50">
      {text}
    </div>
  );
}

function StatusBadge({ status }) {
  const config =
    status === "completed"
      ? { style: "bg-emerald-50 text-emerald-600 border-emerald-100", icon: CheckCircle2 }
      : ["failed", "cancelled", "refunded"].includes(status)
      ? { style: "bg-red-50 text-red-500 border-red-100", icon: XCircle }
      : { style: "bg-amber-50 text-amber-600 border-amber-100", icon: Clock };
  const Icon = config.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border capitalize ${config.style}`}
    >
      <Icon size={14} />
      {humanize(status)}
    </span>
  );
}

function LegStatusBadge({ status }) {
  const style =
    status === "completed"
      ? "bg-emerald-50 text-emerald-600 border-emerald-100"
      : ["failed", "cancelled"].includes(status)
      ? "bg-red-50 text-red-500 border-red-100"
      : status === "provider_state_unknown"
      ? "bg-orange-50 text-orange-600 border-orange-100"
      : "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border capitalize ${style}`}
    >
      {humanize(status)}
    </span>
  );
}


function money(value, currency) {
  return `${currency || ""} ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`.trim();
}

function humanize(value = "") {
  return String(value).replaceAll("_", " ");
}

function formatDate(value) {
  return value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";
}
